import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { loadAuthUser } from '../../services/auth';
import { getUserStorageKey, migrateLegacyData } from '../../utils/storage';
import { AppSettings, Theme } from '../../types';
import zh from '../../i18n/zh.json';
import en from '../../i18n/en.json';
import es from '../../i18n/es.json';

interface AppSettingsContextType extends AppSettings {
  setTheme: (theme: Theme) => void;
  setLanguage: (language: string) => void;
  setNotifications: (notifications: boolean) => void;
  setNotificationTime: (time: string) => void;
  t: (key: string, params?: Record<string, any>) => string;
}

const AppSettingsContext = createContext<AppSettingsContextType | undefined>(undefined);

// 翻译字典来源：外部 JSON
const translations: Record<string, Record<string, string>> = {
  'zh-CN': zh,
  'en-US': en,
  'es-ES': es,
};


interface AppSettingsProviderProps {
  children: ReactNode;
}

export const AppSettingsProvider: React.FC<AppSettingsProviderProps> = ({ children }) => {

  // 检测设备语言
  const getDeviceLanguage = (): string => {
    // 优先从 localStorage 读取用户设置
    const user = loadAuthUser();
    const storageKey = getUserStorageKey('app-settings', user?.id);
    const savedSettings = localStorage.getItem(storageKey);
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        if (parsed.language) {
          return parsed.language;
        }
      } catch (error) {
        console.error('Failed to parse saved settings:', error);
      }
    }
    // 如果没有保存的设置，根据设备语言自动检测
    const deviceLang = navigator.language || (navigator as any).userLanguage || 'en-US';
    // 支持的语言映射
    if (deviceLang.startsWith('zh')) {
      return 'zh-CN';
    } else if (deviceLang.startsWith('en')) {
      return 'en-US';
    } else if (deviceLang.startsWith('es')) {
      return 'es-ES';
    }
    // 默认返回英文
    return 'en-US';
  };

  const [settings, setSettings] = useState<AppSettings>({
    theme: 'dark',
    language: getDeviceLanguage(),
    notifications: true,
    notificationTime: '23:00'
  });

  // 从localStorage加载设置（保留用户手动设置的语言）
  useEffect(() => {
    const user = loadAuthUser();
    const storageKey = getUserStorageKey('app-settings', user?.id);
    
    // Migrate settings
    if (user?.id) migrateLegacyData('app-settings', user.id);

    const savedSettings = localStorage.getItem(storageKey);
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setSettings(prev => ({ ...prev, ...parsed }));
      } catch (error) {
        console.error('Failed to load app settings:', error);
      }
    }
  }, []);

  // 保存设置到localStorage
  const updateSettings = (newSettings: Partial<AppSettings>) => {
    setSettings(prev => {
      const updated = { ...prev, ...newSettings };
      const user = loadAuthUser();
      const storageKey = getUserStorageKey('app-settings', user?.id);
      localStorage.setItem(storageKey, JSON.stringify(updated));
      return updated;
    });
  };

  const setTheme = (theme: Theme) => updateSettings({ theme });
  const setLanguage = (language: string) => updateSettings({ language });
  const setNotifications = (notifications: boolean) => updateSettings({ notifications });
  const setNotificationTime = (time: string) => updateSettings({ notificationTime: time });

  // 翻译函数
  const t = (key: string, params?: Record<string, any>): string => {
    const translation = translations[settings.language]?.[key] || translations['zh-CN'][key] || key;

    if (params) {
      return translation.replace(/\{(\w+)\}/g, (match, paramKey) => {
        // 使用 !== undefined 而不是 ||，避免 0 被当作 falsy 值
        return params[paramKey] !== undefined ? String(params[paramKey]) : match;
      });
    }

    return translation;
  };

  const value: AppSettingsContextType = {
    ...settings,
    setTheme,
    setLanguage,
    setNotifications,
    setNotificationTime,
    t
  };

  return (
    <AppSettingsContext.Provider value={value}>
      <div className={`theme-provider ${settings.theme}`}>
        {children}
      </div>
    </AppSettingsContext.Provider>
  );
};

export const useAppSettings = (): AppSettingsContextType => {
  const context = useContext(AppSettingsContext);
  if (context === undefined) {
    throw new Error('useAppSettings must be used within an AppSettingsProvider');
  }
  return context;
};
